/**
 * EntryScreen - Initial screen to select lifecycle stage and area
 */

import React, { useState } from 'react';
import { colors, fonts, spacing, radii, layout, transitions } from '../styles/theme';
import { Phase, PHASE_LABELS, PHASE_EXPLANATIONS, EXAMPLE_AREAS, AREA_DESCRIPTIONS } from '../types';

interface EntryScreenProps {
    onStart: (phase: Phase, area: string) => void;
    onBackToBeginning: () => void;
    participantOneName?: string;
    participantTwoName?: string;
    isLoading: boolean;
    error: string | null;
}

const EntryScreen: React.FC<EntryScreenProps> = ({
    onStart,
    onBackToBeginning,
    participantOneName,
    participantTwoName,
    isLoading,
    error,
}) => {
    const [selectedPhase, setSelectedPhase] = useState<Phase | null>(null);
    const [selectedArea, setSelectedArea] = useState<string>('');

    const handleStart = () => {
        if (selectedPhase && selectedArea) {
            onStart(selectedPhase, selectedArea);
        }
    };

    const phases: Phase[] = ['phase1', 'phase2', 'phase3', 'phase4'];

    return (
        <div style={styles.container}>
            <div style={styles.content}>
                <div style={styles.hero}>
                    <h1 style={styles.title}>
                        The Shopping Bag for<br />
                        Ethics and Legal Issues in VR
                    </h1>
                    {participantOneName && participantTwoName && (
                        <p style={styles.subtitle}>
                            {participantOneName} &amp; {participantTwoName}
                        </p>
                    )}
                </div>

                {error && (
                    <div style={styles.error}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="12" />
                            <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                        <span>{error}</span>
                    </div>
                )}

                {/* Main card */}
                <div style={styles.card}>
                    {/* Lifecycle Stage Selection */}
                    <div style={styles.section}>
                        <div style={styles.sectionHeader}>
                            <span style={styles.stepNumber}>1</span>
                            <h3 style={styles.sectionTitle}>Select Lifecycle Stage</h3>
                        </div>
                        <div style={styles.optionsGrid}>
                            {phases.map((phase) => {
                                const isSelected = selectedPhase === phase;
                                return (
                                    <button
                                        key={phase}
                                        style={{
                                            ...styles.optionButton,
                                            borderColor: isSelected ? colors.text : colors.border,
                                            backgroundColor: isSelected ? colors.bgLight : colors.white,
                                        }}
                                        onClick={(e) => {
                                            setSelectedPhase(phase);
                                            (e.currentTarget as HTMLButtonElement).blur();
                                        }}
                                    >
                                        <span style={{
                                            ...styles.optionPhase,
                                            color: isSelected ? colors.text : colors.textSecondary,
                                        }}>
                                            {phase.replace('phase', 'Phase ')} – {PHASE_EXPLANATIONS[phase]}
                                        </span>
                                        <span style={styles.optionLabel}>{PHASE_LABELS[phase]}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Area Selection */}
                    <div style={styles.section}>
                        <div style={styles.sectionHeader}>
                            <span style={styles.stepNumber}>2</span>
                            <h3 style={styles.sectionTitle}>Select Application Area</h3>
                        </div>
                        <p style={styles.areaSectionNote}>
                            This is used for examples only, it will not change your questions.
                        </p>
                        <div style={styles.areaGrid}>
                            {EXAMPLE_AREAS.map((area) => {
                                const isSelected = selectedArea === area;
                                return (
                                    <button
                                        key={area}
                                        style={{
                                            ...styles.areaButton,
                                            borderColor: isSelected ? colors.text : colors.border,
                                            backgroundColor: isSelected ? colors.bgLight : colors.white,
                                        }}
                                        onClick={(e) => {
                                            setSelectedArea(area);
                                            (e.currentTarget as HTMLButtonElement).blur();
                                        }}
                                    >
                                        <span style={{
                                            ...styles.areaName,
                                            color: isSelected ? colors.text : colors.textSecondary,
                                        }}>
                                            {area}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                        {selectedArea && AREA_DESCRIPTIONS[selectedArea] && (
                            <p style={styles.areaDescription}>
                                {AREA_DESCRIPTIONS[selectedArea]}
                            </p>
                        )}
                    </div>

                    {/* Start Button */}
                    <button
                        style={{
                            ...styles.startButton,
                            ...((!selectedPhase || !selectedArea || isLoading) ? styles.startButtonDisabled : {})
                        }}
                        onClick={handleStart}
                        disabled={!selectedPhase || !selectedArea || isLoading}
                    >
                        {isLoading ? 'Starting...' : 'Start Assessment'}
                        {!isLoading && (
                            <svg 
                                width="20" 
                                height="20" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                stroke="currentColor" 
                                strokeWidth="2"
                                style={{ marginLeft: spacing[2] }}
                            >
                                <line x1="5" y1="12" x2="19" y2="12" />
                                <polyline points="12 5 19 12 12 19" />
                            </svg>
                        )}
                    </button>
                    {isLoading && (
                        <p style={styles.loadingWarning}>
                            The questions may take up to 1 minute to load
                        </p>
                    )}
                </div>

                <button
                    type="button"
                    style={styles.backButton}
                    onClick={onBackToBeginning}
                >
                    ← Back to the beginning
                </button>

                {/* Funding disclaimer */}
                <div style={styles.disclaimer}>
                    <img
                        src={`${process.env.PUBLIC_URL || ''}/vinnova-logo.svg`}
                        alt="Vinnova"
                        style={styles.vinnovaLogo}
                    />
                    <p style={styles.disclaimerText}>
                        Funded under <em>"Legal and ethical analysis and operational guidelines for Virtual Reality"</em> by Vinnova.
                        Reference number 2025-00489. Coordinator: RISE Research Institutes of Sweden AB.
                    </p>
                </div>
            </div>
        </div>
    );
};

const styles: Record<string, React.CSSProperties> = {
    container: {
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.bgLight,
        padding: spacing[6],
    },
    content: {
        width: '100%',
        maxWidth: '560px',
    },
    hero: {
        textAlign: 'center',
        marginTop: spacing[4],
        marginBottom: spacing[8],
    },
    title: {
        fontSize: fonts['3xl'],
        fontWeight: fonts.bold,
        color: colors.text,
        marginBottom: spacing[2],
    },
    subtitle: {
        fontSize: fonts.lg,
        color: colors.textSecondary,
        margin: 0,
    },
    backButton: {
        marginBottom: spacing[4],
        padding: `${spacing[1]} ${spacing[2]}`,
        border: 'none',
        background: 'none',
        color: colors.textSecondary,
        fontSize: fonts.sm,
        cursor: 'pointer',
        textDecoration: 'underline',
        textAlign: 'left' as const,
    },
    error: {
        display: 'flex',
        alignItems: 'center',
        gap: spacing[2],
        backgroundColor: colors.accentLight,
        border: `1px solid ${colors.accent}`,
        color: colors.no,
        padding: `${spacing[3]} ${spacing[4]}`,
        borderRadius: radii.lg,
        marginBottom: spacing[6],
        fontSize: fonts.sm,
    },
    card: {
        backgroundColor: colors.white,
        borderRadius: radii.xl,
        border: `1px solid ${colors.border}`,
        padding: spacing[8],
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
    },
    section: {
        marginBottom: spacing[8],
    },
    sectionHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: spacing[3],
        marginBottom: spacing[4],
    },
    stepNumber: {
        width: '28px',
        height: '28px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.primary,
        color: colors.white,
        fontSize: fonts.xs,
        fontWeight: fonts.semibold,
        borderRadius: radii.full,
    },
    sectionTitle: {
        fontSize: fonts.base,
        fontWeight: fonts.medium,
        color: colors.text,
        margin: 0,
    },
    optionsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: spacing[3],
    },
    optionButton: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: spacing[4],
        border: `1px solid ${colors.border}`,
        borderRadius: radii.md,
        backgroundColor: colors.white,
        cursor: 'pointer',
        transition: `all ${transitions.fast}`,
        textAlign: 'left',
        outline: 'none',
    },
    optionButtonSelected: {
        borderColor: colors.text,
        backgroundColor: colors.bgLight,
    },
    optionPhase: {
        fontSize: fonts.sm,
        fontWeight: fonts.semibold,
        color: colors.textSecondary,
        marginBottom: spacing[1],
    },
    optionPhaseSelected: {
        color: colors.text,
    },
    optionLabel: {
        fontSize: fonts.xs,
        color: colors.textMuted,
    },
    areaSectionNote: {
        margin: `0 0 ${spacing[3]} 0`,
        fontSize: fonts.xs,
        color: colors.textMuted,
        lineHeight: fonts.lineHeightRelaxed,
    },
    areaGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: spacing[2],
    },
    areaButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: `${spacing[3]} ${spacing[2]}`,
        border: `1px solid ${colors.border}`,
        borderColor: colors.border,
        borderRadius: radii.md,
        backgroundColor: colors.white,
        cursor: 'pointer',
        transition: `all ${transitions.fast}`,
        textAlign: 'center',
        outline: 'none',
    },
    areaButtonSelected: {
        borderColor: colors.text,
        backgroundColor: colors.bgLight,
    },
    areaName: {
        fontSize: fonts.sm,
        fontWeight: fonts.medium,
        color: colors.textSecondary,
    },
    areaNameSelected: {
        color: colors.text,
    },
    areaDescription: {
        marginTop: spacing[3],
        padding: spacing[4],
        fontSize: fonts.sm,
        color: colors.textSecondary,
        backgroundColor: colors.bgWarm,
        borderRadius: radii.lg,
        margin: `${spacing[3]} 0 0 0`,
        lineHeight: fonts.lineHeightRelaxed,
    },
    startButton: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: `${spacing[5]} ${spacing[6]}`,
        fontSize: fonts.lg,
        fontWeight: fonts.bold,
        color: colors.white,
        backgroundColor: colors.primary,
        border: 'none',
        borderRadius: radii.lg,
        cursor: 'pointer',
        transition: `all ${transitions.fast}`,
        minHeight: layout.minTouchTarget,
        boxShadow: 'none',
    },
    startButtonDisabled: {
        backgroundColor: colors.textMuted,
        cursor: 'not-allowed',
        opacity: 0.6,
        boxShadow: 'none',
    },
    loadingWarning: {
        marginTop: spacing[4],
        marginBottom: 0,
        textAlign: 'center' as const,
        fontSize: fonts.sm,
        fontWeight: fonts.medium,
        color: colors.textSecondary,
    },
    disclaimer: {
        display: 'flex',
        alignItems: 'center',
        gap: spacing[4],
        marginTop: spacing[8],
        padding: spacing[4],
        backgroundColor: colors.white,
        borderRadius: radii.lg,
        border: `1px solid ${colors.border}`,
    },
    vinnovaLogo: {
        width: '80px',
        height: 'auto',
        flexShrink: 0,
    },
    disclaimerText: {
        fontSize: fonts.xs,
        color: colors.textMuted,
        lineHeight: fonts.lineHeightRelaxed,
        margin: 0,
    },
};

export default EntryScreen;
